import {
  CanvasSource,
  Color,
  Container,
  Sprite,
  Texture,
  type BLEND_MODES,
} from "pixi.js";

import type { IOverlayElementParticles } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";
import {
  PRESET_DEFAULTS,
  drawParticle,
  getSparkTexture,
  particleAlpha,
  stepParticlesFrame,
  type Particle as SimParticle,
  type ParticlePreset,
  type ParticleShape,
} from "../../../../components/AreaOverlay/elements/particlesEngine";

import type { OverlayFrameUpdater } from "./OverlayElementAnimator";

interface IParticlesRenderArgs {
  element: IOverlayElementParticles;
  destination: { width: number; height: number };
}

/**
 * Formas con textura propia. `spark` y `cloud` no tienen una: se componen con
 * dos capas (núcleo `circle` + `halo`), como sus dos pasadas en canvas2d.
 */
export type ParticleShapeTextureKind =
  | Exclude<ParticleShape, "spark" | "cloud">
  | "halo";

export interface IParticleShapeTexture {
  texture: Texture;
  /** Dónde cae la posición de la partícula dentro de la textura (0..1). */
  anchorX: number;
  anchorY: number;
  /** Tamaño de referencia (px de diseño) al que se pintó la forma. */
  ref: number;
  /** Escala del sprite por px de `size`: `size × unitScale` reproduce la forma a ese tamaño. */
  unitScale: number;
}

interface IShapeTextureSpec {
  /**
   * Tamaño al que se pinta la forma. Los rasgos en px absolutos del canvas
   * (trazo del anillo/moneda, el +2 de la moneda) quedan calibrados a este
   * tamaño y escalan con la partícula: es la única aproximación respecto al
   * canvas2d, que los dibuja siempre a 1–1,5 px.
   */
  ref: number;
  /** Caja que ocupa la forma pintada a tamaño `ref`, relativa a la posición de la partícula (px). */
  box: { x: number; y: number; w: number; h: number };
  /** Lado mayor de la textura en texels (sin el borde). */
  texels: number;
}

/** Borde transparente alrededor de la forma para que el filtrado no recorte el antialias. */
export const SHAPE_TEXTURE_PAD = 2;

const SHAPE_TEXTURE_SPECS: Record<
  Exclude<ParticleShapeTextureKind, "halo">,
  IShapeTextureSpec
> = {
  // Rectángulo size × size/2 centrado (confetti); el sprite lo rota.
  rect: { ref: 10, box: { x: -5, y: -2.5, w: 10, h: 5 }, texels: 64 },
  circle: { ref: 8, box: { x: -4, y: -4, w: 8, h: 8 }, texels: 64 },
  // drawHeart pinta desde (x − size/2, y − size/2): el corazón queda a la
  // izquierda de la posición, igual que en el DOM.
  heart: { ref: 19, box: { x: -19, y: -9.5, w: 19, h: 19 }, texels: 64 },
  // Elipse de ancho size + 2 con trazo oscuro de 1 px (a `ref`).
  coin: { ref: 17, box: { x: -10, y: -9, w: 20, h: 18 }, texels: 64 },
  // Trazo de largo size hacia −x, 1,5 px de ancho y puntas redondas; el
  // sprite lo orienta según la velocidad.
  streak: { ref: 19, box: { x: -19.75, y: -0.75, w: 20.5, h: 1.5 }, texels: 128 },
  // Anillo de 1,5 px + brillo arriba a la izquierda (a `ref`).
  ring: { ref: 12, box: { x: -7, y: -7, w: 14, h: 14 }, texels: 64 },
};

/** Fade lineal sin `alphaMax`: alpha 1 a edad 0, así la forma se pinta opaca. */
const OPAQUE_PRESET = PRESET_DEFAULTS.confetti;

const textureFromCanvas = (canvas: HTMLCanvasElement, label: string): Texture =>
  new Texture({
    source: new CanvasSource({
      resource: canvas,
      // Las formas se pintan a 64–128 texels y se muestran a 2–30 px: con
      // mipmaps el trazo fino de la lluvia y el anillo no alias al reducirse.
      autoGenerateMipmaps: true,
      label: `particles:${label}`,
    }),
    label: `particles:${label}`,
  });

/**
 * Pinta la forma con el MISMO `drawParticle` del canvas2d (blanca, opaca,
 * a tamaño `ref`, ampliada k texels por px): la geometría es la del DOM.
 */
const paintShapeTexture = (
  kind: Exclude<ParticleShapeTextureKind, "halo">,
  spec: IShapeTextureSpec,
): IParticleShapeTexture => {
  const k = spec.texels / Math.max(spec.box.w, spec.box.h);
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(spec.box.w * k) + 2 * SHAPE_TEXTURE_PAD;
  canvas.height = Math.ceil(spec.box.h * k) + 2 * SHAPE_TEXTURE_PAD;
  // Posición de la partícula dentro de la textura (texels).
  const ox = SHAPE_TEXTURE_PAD - spec.box.x * k;
  const oy = SHAPE_TEXTURE_PAD - spec.box.y * k;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.setTransform(k, 0, 0, k, ox, oy);
    drawParticle(
      ctx,
      {
        x: 0,
        y: 0,
        vx: 1,
        vy: 0,
        age: 0,
        ttl: 1,
        rot: 0,
        vrot: 0,
        size: spec.ref,
        hue: 0,
        shape: kind,
        phase: 0,
      },
      OPAQUE_PRESET,
      "#ffffff",
      getSparkTexture(),
    );
  }
  return {
    texture: textureFromCanvas(canvas, kind),
    anchorX: ox / canvas.width,
    anchorY: oy / canvas.height,
    ref: spec.ref,
    unitScale: 1 / (spec.ref * k),
  };
};

/** El halo es la misma textura radial de 64 px que el canvas dibuja con `drawImage`. */
const haloTexture = (): IParticleShapeTexture => {
  const canvas = getSparkTexture();
  return {
    texture: textureFromCanvas(canvas, "halo"),
    anchorX: 0.5,
    anchorY: 0.5,
    ref: canvas.width,
    unitScale: 1 / canvas.width,
  };
};

const shapeTextures = new Map<ParticleShapeTextureKind, IParticleShapeTexture>();

/**
 * Textura de una forma: se genera UNA vez y la comparten todos los elementos
 * de partículas (y todos los renderers). Nunca se destruye con ellos.
 */
export const getParticleShapeTexture = (
  kind: ParticleShapeTextureKind,
): IParticleShapeTexture => {
  let entry = shapeTextures.get(kind);
  if (!entry) {
    entry =
      kind === "halo"
        ? haloTexture()
        : paintShapeTexture(kind, SHAPE_TEXTURE_SPECS[kind]);
    shapeTextures.set(kind, entry);
  }
  return entry;
};

export interface IParticleShapeLayer {
  kind: ParticleShapeTextureKind;
  blendMode: BLEND_MODES;
  /** Lleva el color de la partícula; si no, blanco (el halo y la mancha de la nube se pintan sin tinte). */
  tinted: boolean;
  /** Vuelca la partícula simulada al sprite: posición, escala desde `size`, rotación y alpha. */
  apply: (
    sprite: Sprite,
    p: SimParticle,
    alpha: number,
    tex: IParticleShapeTexture,
  ) => void;
}

const uniform =
  (factor: number): IParticleShapeLayer["apply"] =>
  (sprite, p, alpha, tex) => {
    sprite.position.set(p.x, p.y);
    sprite.scale.set(p.size * factor * tex.unitScale);
    sprite.alpha = alpha;
  };

const applyUniform = uniform(1);

const applyCloudDisc = uniform(0.7);

/**
 * Capas (contenedores) por forma, de abajo hacia arriba, con la misma
 * geometría que `drawParticle`. Con dos capas todas las partículas de la
 * primera quedan debajo de las de la segunda (el canvas las intercala por
 * partícula): en sparks y nubes, ambas translúcidas, no se nota.
 */
export const SHAPE_LAYERS: Record<ParticleShape, IParticleShapeLayer[]> = {
  rect: [
    {
      kind: "rect",
      blendMode: "normal",
      tinted: true,
      apply: (sprite, p, alpha, tex) => {
        applyUniform(sprite, p, alpha, tex);
        sprite.rotation = p.rot;
      },
    },
  ],
  circle: [{ kind: "circle", blendMode: "normal", tinted: true, apply: applyUniform }],
  heart: [{ kind: "heart", blendMode: "normal", tinted: true, apply: applyUniform }],
  coin: [
    {
      kind: "coin",
      blendMode: "normal",
      tinted: true,
      apply: (sprite, p, alpha, tex) => {
        // Elipse acortada girando sobre su eje vertical: ancho |cos(rot)|·size + 2
        // (la textura la tiene de ref + 2).
        sprite.position.set(p.x, p.y);
        sprite.scale.set(
          ((Math.abs(Math.cos(p.rot)) * p.size + 2) / (tex.ref + 2)) *
            tex.ref *
            tex.unitScale,
          p.size * tex.unitScale,
        );
        sprite.alpha = alpha;
      },
    },
  ],
  spark: [
    // Núcleo de color (arc de radio 0,8·size) + halo blanco aditivo de 3·size.
    { kind: "circle", blendMode: "normal", tinted: true, apply: uniform(1.6) },
    { kind: "halo", blendMode: "add", tinted: false, apply: uniform(3) },
  ],
  streak: [
    {
      kind: "streak",
      blendMode: "normal",
      tinted: true,
      apply: (sprite, p, alpha, tex) => {
        sprite.position.set(p.x, p.y);
        sprite.rotation = Math.atan2(p.vy, p.vx);
        // Ancho fijo de 1,5 px sea cual sea el largo: en y, 1 px por px de referencia.
        sprite.scale.set(p.size * tex.unitScale, tex.ref * tex.unitScale);
        sprite.alpha = alpha;
      },
    },
  ],
  cloud: [
    // Disco de color al 50 % de alpha (radio 0,35·size) y encima la mancha
    // radial blanca de size × 0,6·size.
    {
      kind: "circle",
      blendMode: "normal",
      tinted: true,
      apply: (sprite, p, alpha, tex) => applyCloudDisc(sprite, p, alpha * 0.5, tex),
    },
    {
      kind: "halo",
      blendMode: "normal",
      tinted: false,
      apply: (sprite, p, alpha, tex) => {
        sprite.position.set(p.x, p.y);
        sprite.scale.set(p.size * tex.unitScale, 0.6 * p.size * tex.unitScale);
        sprite.alpha = alpha;
      },
    },
  ],
  ring: [{ kind: "ring", blendMode: "normal", tinted: true, apply: applyUniform }],
};

/** `hsl(h, s, l)` → 0xRRGGBB con la misma conversión sRGB que hace el canvas. */
export const hslToNumber = (h: number, s: number, l: number): number => {
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number) => {
    const k = (n + h / 30) % 12;
    return Math.round((l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))) * 255);
  };
  return (channel(0) << 16) | (channel(8) << 8) | channel(4);
};

/**
 * Color fijo del elemento/preset como tinte Pixi (y su alpha, si el CSS lo
 * trae). Null → cada partícula va con su hue. Un CSS inválido cae al hue en
 * vez de romper el frame.
 */
const parseFixedColor = (
  css: string | undefined,
): { tint: number; alpha: number } | null => {
  if (!css) return null;
  try {
    const color = new Color(css);
    return { tint: color.toNumber(), alpha: color.alpha };
  } catch {
    return null;
  }
};

/**
 * Sprite cuya textura es COMPARTIDA (cache por forma): no la destruye venga
 * el teardown como venga (`destroy(true)`, `{ children: true }`…). Espejo de
 * `OwnedTextureSprite`.
 */
export class SharedTextureSprite extends Sprite {
  override destroy(options?: Parameters<Sprite["destroy"]>[0]): void {
    const base = typeof options === "boolean" ? { children: options } : (options ?? {});

    super.destroy({ ...base, texture: false, textureSource: false });
  }
}

/**
 * Pool de sprites de una capa: un Container con N sprites de la misma
 * textura que se reutilizan frame a frame (nunca se crean/destruyen por
 * frame; crece cuando hay más partículas vivas que sprites y el sobrante
 * queda `visible = false`). Los sprites de igual textura y blend se
 * batchean: un draw call por capa, sin subidas.
 */
interface IParticleLayerPool {
  layer: IParticleShapeLayer;
  tex: IParticleShapeTexture;
  container: Container;
  sprites: SharedTextureSprite[];
  /** Sprites usados en el frame en curso (los primeros `used` del pool). */
  used: number;
}

/**
 * Pixi equivalente de `OverlayElementParticles` (DOM). Comparte el motor
 * (`particlesEngine`: presets, emit→step, semántica de `intensity`) y dibuja
 * en GPU con sprites batcheados: una textura por forma generada una vez con
 * el mismo `drawParticle` del canvas, y por frame solo se actualizan
 * posición/escala/rotación/alpha/tinte de cada sprite del pool (sin subir
 * texturas). El updater devuelto lo maneja el ticker de RenderFactory; dt
 * independiente del refresh igual que el DOM.
 */
export class OverlayParticlesRenderer {
  private static readonly TARGET_FRAME_MS = 1000 / 60;

  render(
    container: Container,
    args: IParticlesRenderArgs,
  ): OverlayFrameUpdater | null {
    const { element, destination } = args;

    const wrapper = new Container();
    if (element.rotation) {
      const rad = (element.rotation * Math.PI) / 180;
      const pivotX = destination.width / 2;
      const pivotY = destination.height / 2;
      wrapper.pivot.set(pivotX, pivotY);
      wrapper.position.set(
        element.bounds.left + pivotX,
        element.bounds.top + pivotY,
      );
      wrapper.rotation = rad;
    } else {
      wrapper.position.set(element.bounds.left, element.bounds.top);
    }
    container.addChild(wrapper);

    // La simulación corre en px de diseño (w,h), como el DOM; los sprites
    // se dibujan directo en ese espacio (el padre ya enmascara al área).
    const w = destination.width;
    const h = destination.height;
    if (w <= 0 || h <= 0) return null;

    // `opts` es el objeto vivo del elemento: el preset y el color se resuelven
    // por frame (como `renderParticlesFrame` en el DOM), así una mutación en
    // caliente no deja la simulación y el dibujo desincronizados.
    const opts = element.options;
    // Un pool (Container) por capa/textura, en orden de abajo hacia arriba;
    // se reconstruyen si el preset cambia de forma.
    let pools: IParticleLayerPool[] = [];
    let poolsShape: ParticleShape | undefined;
    const ensurePools = (preset: ParticlePreset): void => {
      if (poolsShape === preset.shape) return;
      for (const pool of pools) pool.container.destroy({ children: true });
      poolsShape = preset.shape;
      pools = SHAPE_LAYERS[preset.shape].map((layer) => {
        const pool: IParticleLayerPool = {
          layer,
          tex: getParticleShapeTexture(layer.kind),
          container: new Container(),
          sprites: [],
          used: 0,
        };
        wrapper.addChild(pool.container);
        return pool;
      });
    };
    ensurePools(PRESET_DEFAULTS[opts.preset]);
    // Los pools son propios de este elemento: se destruyen con el wrapper
    // (las factories destruyen los hijos al reconstruir).
    // Huérfano tras un removeChildren() sin destroy (teardown genérico): se
    // destruye solo si nadie lo re-parenta en el mismo tick.
    wrapper.on("removed", () => {
      queueMicrotask(() => {
        if (!wrapper.destroyed && !wrapper.parent) {
          wrapper.destroy({ children: true });
        }
      });
    });

    // Color fijo (elemento o preset) cacheado por su CSS: se re-parsea solo
    // si cambia.
    let fixedCss: string | undefined;
    let fixed: ReturnType<typeof parseFixedColor> = null;
    const resolveFixed = (preset: ParticlePreset) => {
      const css = opts.color ?? preset.defaultColor;
      if (css !== fixedCss) {
        fixedCss = css;
        fixed = parseFixedColor(css);
      }
      return fixed;
    };
    const particles: SimParticle[] = [];

    /** Próximo sprite libre del pool (crea uno si no alcanza). */
    const takeSprite = (pool: IParticleLayerPool): SharedTextureSprite => {
      let sprite = pool.sprites[pool.used];
      if (!sprite) {
        sprite = new SharedTextureSprite(pool.tex.texture);
        sprite.anchor.set(pool.tex.anchorX, pool.tex.anchorY);
        sprite.blendMode = pool.layer.blendMode;
        pool.sprites.push(sprite);
        pool.container.addChild(sprite);
      }
      pool.used += 1;
      sprite.visible = true;
      return sprite;
    };
    let lastElapsed = 0;
    let clock: number | undefined;

    return (elapsedMs: number, clockId?: number) => {
      if (wrapper.destroyed) return true;
      // Reloj nuevo (la capa se vuelve a mostrar) o tiempo hacia atrás: en ese
      // frame dt = 0 y se sigue desde ahí (las partículas vivas se conservan).
      if (clockId !== clock || elapsedMs < lastElapsed) {
        clock = clockId;
        lastElapsed = elapsedMs;
      }
      const dt = Math.min(
        2.5,
        Math.max(
          0,
          (elapsedMs - lastElapsed) / OverlayParticlesRenderer.TARGET_FRAME_MS,
        ),
      );
      lastElapsed = elapsedMs;

      const preset = stepParticlesFrame(particles, opts, w, h, dt);
      ensurePools(preset);
      const fixed = resolveFixed(preset);

      for (const pool of pools) pool.used = 0;
      // De la más nueva a la más vieja (la vieja queda encima), como el canvas.
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        const alpha = particleAlpha(p, preset);
        const tint = fixed ? fixed.tint : hslToNumber(p.hue, 0.85, 0.6);
        for (const pool of pools) {
          const sprite = takeSprite(pool);
          const { layer } = pool;
          if (layer.tinted && sprite.tint !== tint) sprite.tint = tint;
          layer.apply(
            sprite,
            p,
            layer.tinted && fixed ? alpha * fixed.alpha : alpha,
            pool.tex,
          );
        }
      }
      // El sobrante del pool se oculta (queda para el próximo frame).
      for (const pool of pools) {
        for (let k = pool.used; k < pool.sprites.length; k += 1) {
          const sprite = pool.sprites[k];
          if (sprite.visible) sprite.visible = false;
        }
      }
    };
  }
}

export default OverlayParticlesRenderer;
